// ActivityDetailModal
//
// Admin-side detail modal for activities (public.open_matches rows) that
// have a linked blocking booking on /manager/schedule. Reuses
// `useActivityDetail` (same hook the player-side /activities/:id page
// uses) so the data shape stays canonical -- no admin-specific RPC.
//
// First-iteration scope = READ-ONLY. Mirror of MatchDetailModal's
// pattern for league matches:
//   - Activity title + type icon
//   - Effective date/time + court name
//   - Organizer (name from open_matches.created_by -> profiles.full_name)
//   - Status badge + payment_mode badge
//   - Spots filled / total + participants list
//   - Linked booking_id (admin can cross-reference with the schedule grid)
//   - "Öppna spelarvy" CTA -> navigates to /activities/:id for full
//     edit/cancel/approve actions (player surface already supports them
//     for organizer = admin)
//
// All copy in Swedish, consistent with sibling modals.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { sv } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { useActivityDetail } from "@/hooks/useActivityDetail";
import { useAuth } from "@/hooks/useAuth";
import { useOrgHall } from "@/hooks/useOrgHall";
import { ActivityRescheduleDialog, type ActivityRescheduleSource } from "@/components/admin/ActivityRescheduleDialog";
// D10 — adopt the shared shell (operator-locked Fas 1). Container, header,
// body spacing and footer-row come from the shell; domain body + the
// ActivityRescheduleDialog companion stay here.
import { BookingDetailShell } from "@/components/admin/schedule/BookingDetailShell";

interface ActivityDetailModalProps {
  activityId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** shadow-rendering-drag-fix Gap 1B — when set (drag-drop entry point),
   *  the modal auto-opens ActivityRescheduleDialog with the drop slot
   *  pre-filled. null/undefined for the click path (read-only detail). */
  initialRescheduleTarget?: { courtId: string; date: string; time: string } | null;
  /** Called after a successful reschedule so parent can refresh the grid. */
  onRescheduled?: () => void;
}

function paymentModeLabel(mode: string | null | undefined): string {
  switch (mode) {
    case "system":
      return "Betalas i systemet";
    case "external":
      return "Hanteras utanför systemet";
    case "free":
      return "Gratis";
    default:
      return mode ?? "Okänt betalsätt";
  }
}

function statusLabel(status: string | null | undefined): string {
  switch (status) {
    case "open":
      return "Öppen";
    case "full":
      return "Full";
    case "awaiting_booking_approval":
      return "Inväntar godkännande";
    case "booked":
      return "Bokad";
    case "cancelled":
      return "Avbokad";
    case "completed":
      return "Genomförd";
    default:
      return status ?? "—";
  }
}

export function ActivityDetailModal({
  activityId,
  open,
  onOpenChange,
  initialRescheduleTarget,
  onRescheduled,
}: ActivityDetailModalProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { courts: orgCourts } = useOrgHall();
  const { activity, isLoading } = useActivityDetail(activityId);

  const [rescheduleOpen, setRescheduleOpen] = useState(false);

  // Auto-open ActivityRescheduleDialog when modal opens with a drag-drop
  // target. Mirrors MatchDetailModal's Phase D drop behavior.
  useEffect(() => {
    if (open && initialRescheduleTarget) {
      setRescheduleOpen(true);
    }
  }, [open, initialRescheduleTarget]);

  const handleOpenPlayerView = () => {
    onOpenChange(false);
    navigate(`/activities/${activityId}`);
  };

  const handleOpenReschedule = () => {
    setRescheduleOpen(true);
  };

  // Build the reschedule source from the loaded activity. Available only
  // when activity data has loaded.
  const rescheduleSource: ActivityRescheduleSource | null = activity
    ? {
        activityId,
        bookingId: activity.booking_id ?? null,
        currentCourtId: activity.court_id ?? null,
        currentDate: activity.date ?? null,
        currentTime: activity.start_time ?? null,
        durationMinutes: (() => {
          // Derive duration from start/end (preserve whatever the activity
          // currently has; the RPC enforces this too).
          if (!activity.start_time || !activity.end_time) return 90;
          const [sh, sm] = activity.start_time.slice(0, 5).split(":").map(Number);
          const [eh, em] = activity.end_time.slice(0, 5).split(":").map(Number);
          return Math.max(1, (eh * 60 + em) - (sh * 60 + sm));
        })(),
        label: activity.activity_title ?? "Aktivitet",
      }
    : null;

  const courtsList = (orgCourts as Array<{ id: string; name: string }>).map((c) => ({ id: c.id, name: c.name }));

  // D10 — body is split into loading / not-found / loaded variants so the
  // shell's footer slot only renders meaningful actions when activity loaded.
  const body = isLoading ? (
    <div className="space-y-3">
      <Skeleton className="h-6 w-3/4" />
      <Skeleton className="h-4 w-1/2" />
      <Skeleton className="h-20 w-full" />
    </div>
  ) : !activity ? (
    <div className="py-6 text-sm text-muted-foreground text-center">
      Aktiviteten kunde inte hämtas.
    </div>
  ) : (
    <>
      {/* Activity title + 3 badges in BODY (operator-locked C=body — multiple
          wide badges would crowd the header slot). */}
      <div>
        <div className="text-base font-semibold leading-tight">
          {activity.activity_title ?? "Aktivitet"}
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2">
          <Badge variant="secondary">{paymentModeLabel(activity.payment_mode)}</Badge>
          <Badge variant="outline">{statusLabel(activity.status)}</Badge>
          {activity.match_type && (
            <Badge variant="outline">{activity.match_type}</Badge>
          )}
        </div>
      </div>

      <Separator />

      {/* Field grid — switched from 3-col (label,value,value) to 2-col for
          visual unity with SubscriptionDetailDialog's canonical look. */}
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <span className="text-muted-foreground text-xs">Datum</span>
          <p className="font-medium">
            {activity.date
              ? format(parseISO(activity.date), "EEEE d MMMM yyyy", { locale: sv })
              : "—"}
          </p>
        </div>
        <div>
          <span className="text-muted-foreground text-xs">Tid</span>
          <p className="font-medium">
            {activity.start_time?.slice(0, 5) ?? "—"}–{activity.end_time?.slice(0, 5) ?? "—"}
          </p>
        </div>
        <div>
          <span className="text-muted-foreground text-xs">Bana</span>
          <p className="font-medium">{activity.courts?.name ?? "—"}</p>
        </div>
        <div>
          <span className="text-muted-foreground text-xs">Organisatör</span>
          <p className="font-medium">{activity.organizer_profile?.full_name ?? "—"}</p>
        </div>
        <div>
          <span className="text-muted-foreground text-xs">Platser</span>
          <p className="font-medium">
            {activity.spots_filled ?? 0} / {activity.spots_total ?? 0}
          </p>
        </div>
        {activity.booking_id && (
          <div className="col-span-2">
            <span className="text-muted-foreground text-xs">Bokning</span>
            <p className="font-mono text-xs break-all">{activity.booking_id}</p>
          </div>
        )}
      </div>

      {activity.activity_players && activity.activity_players.length > 0 && (
        <>
          <Separator />
          <div>
            <span className="text-muted-foreground text-xs">Deltagare</span>
            <ul className="space-y-1 mt-1">
              {activity.activity_players
                .filter((p) => p.status === "joined")
                .map((p) => (
                  <li key={p.id} className="text-xs">
                    {p.profile?.full_name ?? p.user_id}
                  </li>
                ))}
            </ul>
          </div>
        </>
      )}
    </>
  );

  // Footer renders only when activity is loaded (loading/not-found = no actions).
  const footer = !isLoading && activity ? (
    <>
      <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
        Stäng
      </Button>
      <Button variant="outline" size="sm" onClick={handleOpenReschedule}>
        Flytta aktivitet
      </Button>
      <Button size="sm" onClick={handleOpenPlayerView}>
        Öppna spelarvy
      </Button>
    </>
  ) : null;

  return (
    <>
      <BookingDetailShell
        open={open}
        onOpenChange={onOpenChange}
        title="Aktivitetsdetaljer"
        subtitle="Aktivitet skapad i Boply — bana blockad via kopplad bokning."
        footer={footer}
      >
        {body}
      </BookingDetailShell>

      {/* shadow-rendering-drag-fix Gap 1B — reschedule dialog. Opens via
          "Flytta aktivitet" button or auto-opens when modal received an
          initialRescheduleTarget from a schedule-grid drag-drop.
          D10 — moved from inside the outer Dialog to sibling position
          (Radix supports nested Dialog instances via context). */}
      {rescheduleSource && (
        <ActivityRescheduleDialog
          open={rescheduleOpen}
          onOpenChange={setRescheduleOpen}
          source={rescheduleSource}
          courts={courtsList}
          initialTarget={initialRescheduleTarget ?? null}
          adminId={user?.id}
          onRescheduled={() => {
            setRescheduleOpen(false);
            onRescheduled?.();
            onOpenChange(false);
          }}
        />
      )}
    </>
  );
}
